import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

const prefersReducedMotion = (): boolean =>
	typeof window !== "undefined" &&
	window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const triggers: ScrollTrigger[] = [];
const track = (t: ScrollTrigger | ScrollTrigger[] | undefined): void => {
	if (!t) return;
	if (Array.isArray(t)) triggers.push(...t);
	else triggers.push(t);
};

/* ---------- Petal burst helper (used by heading reveals) ---------- */

function burstPetals(host: HTMLElement, count = 6): void {
	if (prefersReducedMotion()) return;
	const palette = ["#e8b8c5", "#a8d5b8", "#f5e8ed", "#c8f04a"];
	for (let i = 0; i < count; i++) {
		const p = document.createElement("span");
		p.className = "petal-burst-particle";
		p.style.background = palette[i % palette.length];
		// initial position — near heading start
		const x0 = Math.random() * 40;
		const y0 = Math.random() * 20;
		p.style.left = `${x0}px`;
		p.style.top = `${y0}px`;
		host.appendChild(p);

		const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
		const dist = 70 + Math.random() * 90;
		const dx = Math.cos(angle) * dist;
		const dy = Math.sin(angle) * dist;
		const rot = (Math.random() - 0.5) * 240;
		const dur = 1.1 + Math.random() * 0.7;

		gsap.fromTo(
			p,
			{ opacity: 0, scale: 0.3, x: 0, y: 0, rotation: 0 },
			{
				opacity: 0.85,
				scale: 1,
				x: dx * 0.4,
				y: dy * 0.4,
				rotation: rot * 0.4,
				duration: 0.25,
				ease: "power2.out",
			},
		);
		gsap.to(p, {
			x: dx,
			y: dy,
			rotation: rot,
			opacity: 0,
			scale: 0.6,
			duration: dur,
			delay: 0.25,
			ease: "power2.out",
			onComplete: () => p.remove(),
		});
	}
}

/* ---------- Hero H1 line-by-line reveal ---------- */

function splitH1IntoLines(el: HTMLElement): HTMLElement[] {
	// The hero H1 is short; split by sentence-ending periods preserved.
	// Source: "Один <em>лендинг</em>. Один <em>бот</em>. Один заказ."
	// We split into three lines of the form "Один <em>X</em>." / "Один заказ."
	const html = el.innerHTML;
	const parts = html.split(/\.\s*/).filter((p) => p.trim().length);
	el.innerHTML = "";
	const out: HTMLElement[] = [];
	parts.forEach((part, i) => {
		const wrap = document.createElement("span");
		wrap.className = "hero__line";
		const inner = document.createElement("span");
		inner.className = "hero__line-inner";
		inner.innerHTML = part + (i < parts.length - 1 ? "." : ".");
		wrap.appendChild(inner);
		el.appendChild(wrap);
		out.push(inner);
	});
	return out;
}

export function initHeroAnim(): void {
	const h1 = document.querySelector<HTMLElement>("#hero .hero__h1");
	const sub = document.querySelector<HTMLElement>("#hero [data-hero-sub]");
	const ctas = gsap.utils.toArray<HTMLElement>("#hero [data-hero-cta]");
	if (!h1) return;

	if (prefersReducedMotion()) {
		gsap.set([h1, sub, ...ctas].filter(Boolean), { opacity: 1, x: 0, y: 0 });
		return;
	}

	const lines = splitH1IntoLines(h1);
	gsap.set(lines, { x: -20, opacity: 0 });
	if (sub) gsap.set(sub, { y: 16, opacity: 0 });
	gsap.set(ctas, { y: 12, opacity: 0 });

	const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
	tl.to(lines, {
		x: 0,
		opacity: 1,
		duration: 0.7,
		stagger: 0.25,
	});
	if (sub) tl.to(sub, { y: 0, opacity: 1, duration: 0.5 }, "-=0.4");
	tl.to(ctas, { y: 0, opacity: 1, duration: 0.5, stagger: 0.1 }, "-=0.2");
}

/* ---------- Generic section reveal with petal burst on heading ---------- */

function revealSection(section: HTMLElement): void {
	const heading = section.querySelector<HTMLElement>(".section__title");
	// NB: do NOT include .flow here — the flow section has its own
	// arrow-driven choreography in initFlowArrows() that reveals each node
	// in sync with the arrow drawing into it.
	const targets = Array.from(
		section.querySelectorAll<HTMLElement>(
			".section__title, .section__lead, .combo-grid, .combo-total, .case-grid, .metrics-grid, .price-box, .steps, .faq, .order-form",
		),
	);
	if (!targets.length) return;

	if (prefersReducedMotion()) {
		gsap.set(targets, { opacity: 1, y: 0 });
		return;
	}

	gsap.set(targets, { opacity: 0, y: 28, willChange: "transform, opacity" });

	const tl = gsap.timeline({
		scrollTrigger: {
			trigger: section,
			start: "top 78%",
			once: true,
		},
	});
	track(tl.scrollTrigger);

	tl.to(targets, {
		opacity: 1,
		y: 0,
		duration: 0.7,
		ease: "power3.out",
		stagger: 0.12,
		onComplete: () => gsap.set(targets, { willChange: "auto" }),
	});

	if (heading) {
		tl.add(() => burstPetals(heading, 6), "-=0.45");
	}
}

export function initSectionReveals(): void {
	const sections = document.querySelectorAll<HTMLElement>(
		"#flow, #combo, #case, #effect, #price, #how, #faq, #order",
	);
	sections.forEach((s) => revealSection(s));
}

/* ---------- Flow arrows: SVG draw-in + traveling petal ---------- */

export function initFlowArrows(): void {
	const arrows = document.querySelectorAll<SVGSVGElement>(
		"#flow [data-flow-arrow] svg",
	);
	const nodes = gsap.utils.toArray<HTMLElement>("#flow [data-flow-node]");
	if (!arrows.length) return;

	// nodes already animated by revealSection; layer in arrow draw + petal
	// Nodes stay visible by default; we only add a subtle enter for nodes 2-4
	// timed to the arrow drawing into them. Node 1 is visible immediately.
	if (prefersReducedMotion()) {
		arrows.forEach((svg) => {
			const line = svg.querySelector<SVGPathElement>(".flow__arrow-line");
			const head = svg.querySelector<SVGPolygonElement>(".flow__arrow-head");
			if (line) line.style.strokeDashoffset = "0";
			if (head) head.style.opacity = "1";
		});
		gsap.set(nodes, { opacity: 1, y: 0 });
		return;
	}

	// Soft initial state — slight lift, full opacity remains
	gsap.set(nodes, { y: 14, opacity: 0.95 });

	arrows.forEach((svg, idx) => {
		const line = svg.querySelector<SVGPathElement>(".flow__arrow-line");
		const head = svg.querySelector<SVGPolygonElement>(".flow__arrow-head");
		if (!line) return;

		const len = line.getTotalLength?.() ?? 100;
		line.style.strokeDasharray = `${len}`;
		line.style.strokeDashoffset = `${len}`;
		if (head) head.style.opacity = "0";

		// Travelling petal (SVG circle styled via .flow__arrow-petal)
		const ns = "http://www.w3.org/2000/svg";
		const petal = document.createElementNS(ns, "ellipse");
		petal.setAttribute("rx", "2.4");
		petal.setAttribute("ry", "3.4");
		petal.setAttribute("fill", "#e8b8c5");
		petal.setAttribute("opacity", "0");
		petal.setAttribute("class", "flow__arrow-petal");
		svg.appendChild(petal);

		const prevNode = nodes[idx];
		const nextNode = nodes[idx + 1];

		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: svg,
				start: "top 80%",
				once: true,
			},
		});
		track(tl.scrollTrigger);

		if (prevNode) {
			tl.to(prevNode, {
				opacity: 1,
				y: 0,
				duration: 0.45,
				ease: "power3.out",
			});
		}
		tl.to(
			line,
			{
				strokeDashoffset: 0,
				duration: 1.2,
				ease: "power2.inOut",
			},
			"-=0.15",
		);
		tl.to(
			petal,
			{
				opacity: 1,
				motionPath: {
					path: line,
					align: line,
					alignOrigin: [0.5, 0.5],
				},
				duration: 1.2,
				ease: "power2.inOut",
			},
			"<",
		);
		tl.to(petal, { opacity: 0, duration: 0.3 }, "-=0.1");
		if (head) {
			tl.to(head, { opacity: 1, duration: 0.3 }, "-=0.3");
		}
		if (nextNode) {
			tl.to(
				nextNode,
				{
					opacity: 1,
					y: 0,
					duration: 0.45,
					ease: "power3.out",
				},
				"-=0.2",
			);
		}
	});

	// Settle all nodes to fully visible when flow enters viewport — safety net
	const settleSt = ScrollTrigger.create({
		trigger: "#flow",
		start: "top 85%",
		once: true,
		onEnter: () => {
			gsap.to(nodes, {
				opacity: 1,
				y: 0,
				duration: 0.6,
				ease: "power3.out",
				stagger: 0.05,
			});
		},
	});
	track(settleSt);
}

/* ---------- Calculator (kept for compatibility, no #calculator in DOM) ---------- */

type CalcChangeHandler = (newValue: number) => void;

export function initCalculator(onChange?: CalcChangeHandler): void {
	const output = document.querySelector<HTMLElement>(
		"#calculator [data-calc-output]",
	);
	if (!output) return;

	const formatMoney = (n: number): string =>
		new Intl.NumberFormat("ru-RU").format(Math.round(n)) + " ₽";

	const state = { val: Number(output.dataset.calcInitial ?? "0") };
	output.textContent = formatMoney(state.val);

	const updateTo = (target: number): void => {
		if (prefersReducedMotion()) {
			state.val = target;
			output.textContent = formatMoney(target);
			onChange?.(target);
			return;
		}
		gsap.to(state, {
			val: target,
			duration: 0.4,
			ease: "power2.out",
			snap: { val: 1 },
			onUpdate: () => {
				output.textContent = formatMoney(state.val);
			},
			onComplete: () => onChange?.(target),
		});
	};
	(window as unknown as { __calcUpdate?: (n: number) => void }).__calcUpdate =
		updateTo;
}

/* ---------- Metric counter reveal ---------- */

export function initMetricCounters(): void {
	const metrics = gsap.utils.toArray<HTMLElement>("#effect .metric__num");
	metrics.forEach((el) => {
		const text = el.textContent ?? "";
		const num = parseFloat(text.replace(/[^\d.-]/g, ""));
		if (!isFinite(num) || num === 0) return;
		const prefix = text.replace(/-?\d+([.,]\d+)?/, "").startsWith("+")
			? "+"
			: "";
		const suffix = text.includes("%") ? "%" : "";
		const isRange = /\d+[–-]\d+/.test(text);
		if (isRange || prefersReducedMotion()) return;

		const obj = { v: 0 };
		const st = ScrollTrigger.create({
			trigger: el,
			start: "top 85%",
			once: true,
			onEnter: () => {
				gsap.to(obj, {
					v: num,
					duration: 1.2,
					ease: "power2.out",
					snap: { v: 1 },
					onUpdate: () => {
						el.textContent = `${prefix}${Math.round(obj.v)}${suffix}`;
					},
				});
			},
		});
		track(st);
	});
}

/* ---------- Boot ---------- */

export function initMotion(): void {
	if (document.fonts && document.fonts.ready) {
		document.fonts.ready.then(() => ScrollTrigger.refresh());
	}

	initHeroAnim();
	initSectionReveals();
	initFlowArrows();
	initMetricCounters();
	initCalculator();

	// Safety: force-reveal anything still hidden after 4s
	setTimeout(() => {
		ScrollTrigger.refresh();
		const stuck = document.querySelectorAll<HTMLElement>(
			"#flow [data-flow-node], .section__title, .section__lead, .combo-grid, .case-grid, .metrics-grid, .price-box, .steps, .faq, .order-form",
		);
		stuck.forEach((el) => {
			const op = parseFloat(getComputedStyle(el).opacity);
			if (op < 0.05) {
				gsap.set(el, {
					clearProps: "opacity,y,x,scale,transform,willChange",
				});
				el.style.opacity = "1";
			}
		});
	}, 4000);
}

export function destroyAll(): void {
	triggers.forEach((t) => t.kill());
	triggers.length = 0;
	ScrollTrigger.getAll().forEach((t) => t.kill());
	gsap.globalTimeline.clear();
}
